"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mediaUploadQuerySchema = exports.MEDIA_UPLOAD_FIELD = exports.IMAGE_MEDIA_KINDS = exports.MAX_VIDEO_UPLOAD_BYTES = exports.MAX_IMAGE_UPLOAD_BYTES = exports.ALLOWED_VIDEO_MIME_TYPES = exports.ALLOWED_IMAGE_MIME_TYPES = exports.updateElectronicsProviderSchema = exports.createElectronicsProviderSchema = exports.EPAYMENT_PROVIDERS = exports.appListQuerySchema = exports.updateAppSchema = exports.createAppSchema = exports.gameListQuerySchema = exports.updateGameSchema = exports.createGameSchema = exports.devicePathSchema = exports.gameAccountListQuerySchema = exports.setAccountVaultPasswordSchema = exports.ACCOUNT_VAULT_PASSWORD_MIN_LENGTH = exports.revealGameAccountSchema = exports.GAME_ACCOUNT_SECRET_FIELDS = exports.bulkAssignHomeDevicesSchema = exports.bulkLinkGameAccountsSchema = exports.updateGameAccountSchema = exports.createGameAccountSchema = exports.gameAccountAllowanceSchema = exports.zPrinterTestJobPayload = exports.updatePrinterSchema = exports.createPrinterSchema = exports.updateRefillBonusTierSchema = exports.createRefillBonusTierSchema = exports.updateGeneralSettingsSchema = exports.hotkeyCombo = exports.HOTKEY_MODIFIERS = exports.timeOfDay = exports.MEDIA_ASSET_KINDS = exports.PRINTER_CONNECTIONS = exports.SERVICE_CHARGE_SCOPES = exports.PORTAL_PAYMENT_METHODS = exports.ROUNDING_RULES = void 0;
exports.resolvePointsName = resolvePointsName;
exports.resolveExecutablePath = resolveExecutablePath;
exports.isImageMediaKind = isImageMediaKind;
const zod_1 = require("zod");
const launch_target_1 = require("./launch-target");
const money_1 = require("./money");
Object.defineProperty(exports, "ROUNDING_RULES", { enumerable: true, get: function () { return money_1.ROUNDING_RULES; } });
const network_1 = require("./network");
const portal_sections_1 = require("./portal-sections");
/*
 * REMOVED 2026-09-24 (owner, after the settings audit: "you can remove these:
 * 1. Cost Type 2. Show 3Ball 3. Enable Task Manager 4. public page 5. device
 * groups page"). Cost Type, Show 3ball and Enable Task Manager were never read
 * by anything that bills, stocks or locks a PC, so they left the DTO and the
 * update schema entirely. Their database columns stay, unused, so nothing the
 * owner once saved is destroyed.
 */
/*
 * REMOVED 2026-10-05 with venue-account borrowing (owner: the launchers' terms
 * do not allow lending accounts). SPEC §6.1 "Platform accounts in the portal"
 * only ordered the Just Play list, and that list is gone, so the mode left the
 * DTO and the update schema entirely. `VenueSettings.platformAccountMode` stays
 * in the database, unread. The config push still sends
 * `platformAccountStrategy`, always 'fixed', because every deployed agent
 * validates that frame against a schema that requires it (events.ts).
 */
/**
 * SPEC §6.1 "Portal Payment Methods" — how a portal (kiosk Shop) order is
 * paid. A RULE the API enforces (`portalOrderPayment` in
 * apps/api/src/portal/portal-shop.service.ts), not a default: the cart offers
 * exactly what it allows, and an order asking for anything else is refused
 * `portal_payment_not_allowed`.
 *
 *  - `VISITOR` (every venue's default) — visitors may order too. An account
 *    pays from its balance as it orders (`BALANCE`); a walk-in, who has no
 *    balance, orders onto the sitting's tab and pays at the counter
 *    (`COUNTER`).
 *  - `ACCOUNT_BALANCE` — kiosk orders are paid from a balance, full stop. An
 *    account pays as it orders; a walk-in cannot order from the PC at all and
 *    is sent to the counter to order there.
 *  - `PAY_AT_CASHIER` — nothing is paid at the PC. Every order, account or
 *    walk-in, goes on the tab and is paid at the counter, and the wallet
 *    leaves it there at sign-out too.
 *
 * The value reaches the kiosk as `PortalMenu.paymentMethod`.
 */
exports.PORTAL_PAYMENT_METHODS = ['VISITOR', 'ACCOUNT_BALANCE', 'PAY_AT_CASHIER'];
/**
 * Which part of a bill the venue's service charge rides on (owner,
 * 2026-09-23): the cafe/kitchen/shisha ITEMS, the RESERVATIONS (play time), or
 * BOTH. Mirrors the Prisma enum `ServiceChargeScope`.
 */
exports.SERVICE_CHARGE_SCOPES = ['ITEMS', 'RESERVATIONS', 'BOTH'];
exports.PRINTER_CONNECTIONS = ['TCP', 'USB'];
exports.MEDIA_ASSET_KINDS = [
    'IDLE_VIDEO',
    'LOGO',
    'GAME_COVER',
    'GAME_TRAILER',
    'APP_ICON',
    'PRODUCT_IMAGE',
    'CATEGORY_IMAGE',
    'PRIZE_IMAGE',
    'CHALLENGE_IMAGE',
    'AVATAR',
    'DEVICE_IMAGE',
    'ATTACHMENT',
    'WIFI_QR',
];
/* -------------------------------------------------------------------------- */
/* Shared field validators                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Opening/Closing Time — "HH:mm", 24-hour, local venue time.
 *
 * SPEC-CRITICAL: these two define the business day, and every report filters on
 * the `businessDate` derived from them. A malformed value here would silently
 * mis-bucket every sale, so the format is pinned rather than parsed leniently:
 * no seconds, no "9:00", no am/pm, no timezone riding along.
 *
 * Closing MAY be earlier than opening — "09:00 -> 08:00" is a venue that trades
 * through midnight, which is the normal case here, so the two are deliberately
 * NOT cross-validated as an ordered pair.
 */
exports.timeOfDay = zod_1.z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:mm, e.g. 09:00 or 23:30.');
/** Money entered by the owner as whole IQD; converted to millidinars server-side. */
const iqdAmount = zod_1.z
    .number()
    .int('Amounts are whole dinars — IQD has no useful sub-unit.')
    .min(0, 'Amount cannot be negative.')
    .max(1_000_000_000_000);
/**
 * §6.1 Central Device IP: the venue MAIN-PC's IPv4 address on the venue LAN
 * (the machine running the print bridge). Every PC is told to fetch its idle
 * video from it first (`config.push` `lanMediaBaseUrl`, the bridge's port
 * 8478), so IPv4 ONLY since 2026-09-24 — see {@link isIpv4Address}. It once
 * took any hostname, from the days it was the host the API itself would run
 * on; a hostname or `host:port` saved now would reach the PCs as no LAN
 * source at all, silently.
 */
const centralDeviceIpAddress = zod_1.z
    .string()
    .trim()
    .refine(network_1.isIpv4Address, 'Enter the main PC’s IPv4 address, like 192.168.1.150 — no port, no name.');
/**
 * A staff-app accent colour, `#rrggbb`. Six digits only: the colour input the
 * Venue page renders always produces six, and the CSS variable it feeds is
 * compared as a string when the theme decides whether anything changed.
 */
const hexColor = zod_1.z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #2DD4BF.');
/**
 * The word to print for the loyalty currency, in the language being read.
 *
 * ONE FUNCTION BECAUSE THERE IS ONE RULE, and it is easy to get subtly wrong in
 * ten places: a venue's chosen name is a brand and is NOT translated — "Escape
 * Coins" stays "Escape Coins" on the Arabic screen, the same way a shop's name
 * does — but the fallback must be, or an unnamed venue shows an English word to
 * every Kurdish customer.
 *
 * @param configured `VenueSettings.pointsName`, or null.
 * @param fallback   The caller's own translation of "points" (`t('points')`).
 */
function resolvePointsName(configured, fallback) {
    const trimmed = configured?.trim() ?? '';
    return trimmed.length > 0 ? trimmed : fallback;
}
/**
 * Hot-key combo, e.g. "Control+Shift+I".
 *
 * Stored as the combo string the agent matches on. At least one modifier is
 * required: a bare letter would fire while a customer types in the portal.
 */
exports.HOTKEY_MODIFIERS = ['Control', 'Alt', 'Shift', 'Meta'];
exports.hotkeyCombo = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(60)
    .refine((value) => {
    const parts = value.split('+').map((part) => part.trim());
    if (parts.length < 2)
        return false;
    const key = parts.at(-1);
    const modifiers = parts.slice(0, -1);
    if (!key || key.length === 0)
        return false;
    return modifiers.every((modifier) => exports.HOTKEY_MODIFIERS.includes(modifier));
}, { message: 'Use at least one modifier plus a key, e.g. Control+Shift+I.' });
/**
 * The Confirm button on §6.1 submits the whole page, so every field is
 * optional here and only what changed is written.
 */
exports.updateGeneralSettingsSchema = zod_1.z.object({
    // ---- Left column -------------------------------------------------------
    /** Writes `Venue.name`. Required to be non-blank: it heads every receipt. */
    venueName: zod_1.z.string().trim().min(1, 'Venue name is required.').max(120).optional(),
    /**
     * Write `Venue.brandPrimaryColor` / `brandSecondaryColor`. Null puts the
     * staff app back on its built-in colours. The auth payload keeps reading the
     * same two columns, so a change shows on the next sign-in or refresh.
     */
    primaryColor: hexColor.optional().nullable(),
    secondaryColor: hexColor.optional().nullable(),
    slogan: zod_1.z.string().trim().max(200).optional().nullable(),
    /** null clears the logo — that is the "delete" affordance next to the upload. */
    logoMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    currencySymbol: zod_1.z.string().trim().min(1, 'Currency symbol is required.').max(8).optional(),
    enableBalanceRefund: zod_1.z.boolean().optional(),
    monthlyTarget: iqdAmount.optional(),
    roundingRule: zod_1.z.enum(money_1.ROUNDING_RULES).optional(),
    // Capped at 10,000: a step larger than most bills would round a coffee to a
    // banknote, and a typo of 250000 must not be able to do that silently.
    checkoutRoundingStepIqd: zod_1.z.coerce.number().int().min(0).max(10_000).optional(),
    // No `.default()` on either: the page submits whole, but a PATCH that omits
    // them must leave the stored charge alone, not reset it to 0 / BOTH.
    serviceChargePercent: zod_1.z.number().int().min(0).max(100).optional(),
    serviceChargeScope: zod_1.z.enum(exports.SERVICE_CHARGE_SCOPES).optional(),
    enableBootCampOffer: zod_1.z.boolean().optional(),
    enableReservationAlerts: zod_1.z.boolean().optional(),
    enableNegativeSales: zod_1.z.boolean().optional(),
    defaultPrinterId: zod_1.z.string().min(1).optional().nullable(),
    copyPrintingCount: zod_1.z.number().int().min(1, 'At least one copy.').max(10).optional(),
    printingPhone: zod_1.z.string().trim().max(40).optional().nullable(),
    printingAddress: zod_1.z.string().trim().max(200).optional().nullable(),
    // ---- Right column ------------------------------------------------------
    enablePortalAccountCreation: zod_1.z.boolean().optional(),
    /**
     * Replaces the whole hidden list; omitted leaves it alone. Comes out
     * canonical, and a list hiding every tab is refused against this field
     * (`PORTAL_LAST_TAB_MESSAGE`).
     */
    portalHiddenSections: portal_sections_1.portalHiddenSectionsSchema.optional(),
    hotkeyCombo: exports.hotkeyCombo.optional(),
    /**
     * Plaintext ONLY in transit, hashed with Argon2id before it is stored.
     * Omit to leave unchanged; null to clear it.
     */
    hotkeyPassword: zod_1.z.string().min(4, 'Use at least 4 characters.').max(200).optional().nullable(),
    enableHotkeyPassword: zod_1.z.boolean().optional(),
    enableCardLogin: zod_1.z.boolean().optional(),
    cardLoginHoldSeconds: zod_1.z.number().int().min(1).max(30).optional(),
    enablePortalOrders: zod_1.z.boolean().optional(),
    portalPaymentMethod: zod_1.z.enum(exports.PORTAL_PAYMENT_METHODS).optional(),
    openingTime: exports.timeOfDay.optional(),
    closingTime: exports.timeOfDay.optional(),
    /** Null clears it; omitted leaves it. */
    centralDeviceIp: centralDeviceIpAddress.optional().nullable(),
    // ---- Behaviour knobs ---------------------------------------------------
    aboutToEndThresholdMinutes: zod_1.z.number().int().min(1).max(120).optional(),
    defaultLowStockThreshold: zod_1.z.number().int().min(0).max(100_000).optional(),
    hotkeyAutoRelockMinutes: zod_1.z.number().int().min(1).max(240).optional(),
    /**
     * Null clears it, putting the venue back on the reader's own word for
     * "points". A BLANK CLEARS IT TOO, and is normalised here rather than in a
     * handler: emptying the box is how staff undo a name, and `""` stored as-is
     * would be a name — an empty one — that renders the currency as nothing at
     * all on every screen. Omitting the field leaves the stored value alone.
     */
    pointsName: zod_1.z
        .string()
        .trim()
        .max(40)
        .optional()
        .nullable()
        .transform((value) => (value == null ? value : value || null)),
    // ---- Wave 3: loyalty & cashback rates, hero video, sign-out combo -------
    pointsPerThousandIqd: zod_1.z.number().int().min(0).max(100_000).optional(),
    pointsPerHourPlayed: zod_1.z.number().int().min(0).max(100_000).optional(),
    refillBonusFlatPercent: zod_1.z.number().int().min(0).max(100).optional(),
    /** null clears the hero video, same affordance as the logo. */
    heroMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    /** null takes the wi-fi QR off the sign-in screen again. */
    wifiQrMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    /** Same syntax as hotkeyCombo; null switches the sign-out combo off. */
    signOutHotkeyCombo: exports.hotkeyCombo.optional().nullable(),
});
/* -------------------------------------------------------------------------- */
/* Wave 3 — refill cashback tiers                                             */
/* -------------------------------------------------------------------------- */
/**
 * One cashback tier: refills at or above `minAmountIqd` earn `bonusPercent`.
 * The HIGHEST matching tier wins outright (tiers do not stack with the flat
 * rate — `resolveRefillBonus` in customers.ts is the arbiter both the API
 * and every preview call).
 */
exports.createRefillBonusTierSchema = zod_1.z.object({
    minAmountIqd: iqdAmount,
    bonusPercent: zod_1.z.number().int().min(1, 'A 0% tier does nothing.').max(100),
});
/** Explicit — never `.partial()` (zod 4 keeps `.default()`s). */
exports.updateRefillBonusTierSchema = zod_1.z
    .object({
    minAmountIqd: iqdAmount.optional(),
    bonusPercent: zod_1.z.number().int().min(1).max(100).optional(),
    isActive: zod_1.z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to change.' });
/**
 * A printer is either TCP (host:port, 9100) or USB (exact Windows printer name
 * on the main PC) — never both, so the irrelevant half is refused rather than
 * quietly ignored.
 */
exports.createPrinterSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(1, 'Printer name is required.').max(40),
    connection: zod_1.z.enum(exports.PRINTER_CONNECTIONS),
    host: zod_1.z.string().trim().max(255).optional().nullable(),
    port: zod_1.z.number().int().min(1).max(65_535).optional().nullable().default(9100),
    windowsPrinterName: zod_1.z.string().trim().max(200).optional().nullable(),
    isActive: zod_1.z.boolean().default(true),
})
    .refine((value) => value.connection !== 'TCP' || (value.host?.trim().length ?? 0) > 0, {
    message: 'A TCP printer needs a host address.',
    path: ['host'],
})
    .refine((value) => value.connection !== 'USB' || (value.windowsPrinterName?.trim().length ?? 0) > 0, {
    message: 'A USB printer needs the exact Windows printer name.',
    path: ['windowsPrinterName'],
});
/**
 * Edit a printer. Explicit rather than `createPrinterSchema.partial()`: zod 4
 * keeps `.default()`s through `.partial()`, and a rename would then silently
 * reset the port to 9100 and re-activate a switched-off printer.
 *
 * The TCP-needs-a-host / USB-needs-a-name rule is judged by the SERVER on the
 * row as it will be after the edit, not here: a PATCH that only renames a
 * printer carries neither half, and is fine.
 */
exports.updatePrinterSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(40).optional(),
    connection: zod_1.z.enum(exports.PRINTER_CONNECTIONS).optional(),
    host: zod_1.z.string().trim().max(255).optional().nullable(),
    port: zod_1.z.number().int().min(1).max(65_535).optional().nullable(),
    windowsPrinterName: zod_1.z.string().trim().max(200).optional().nullable(),
    isActive: zod_1.z.boolean().optional(),
});
/**
 * The stored payload of a `TEST` print job (`PrintJob.kind = TEST`).
 *
 * `lines` is what the renderer prints, one per line — the same `{ lines }`
 * shape its plain-text fallback already accepts, so a test page needs no
 * renderer of its own. The rest is context for the queue screen and the audit
 * trail: which printer, who pressed Test, and when.
 */
exports.zPrinterTestJobPayload = zod_1.z.object({
    kind: zod_1.z.literal('TEST'),
    printerName: zod_1.z.string().min(1).max(40),
    requestedBy: zod_1.z.string().max(120).nullable(),
    /** ISO instant the button was pressed. */
    requestedAt: zod_1.z.string(),
    lines: zod_1.z.array(zod_1.z.string().max(200)).min(1).max(60),
});
exports.gameAccountAllowanceSchema = zod_1.z.object({
    gameId: zod_1.z.string().min(1),
    deviceIds: zod_1.z.array(zod_1.z.string().min(1)).default([]),
});
exports.createGameAccountSchema = zod_1.z.object({
    accountName: zod_1.z.string().trim().min(1, 'Account name is required.').max(80),
    username: zod_1.z.string().trim().min(1, 'Username is required.').max(120),
    password: zod_1.z.string().min(1, 'Password is required.').max(200),
    /** Steam, Epic, Ubisoft, EA… free text: new storefronts appear constantly. */
    company: zod_1.z.string().trim().min(1, 'Company is required.').max(60),
    gameIds: zod_1.z.array(zod_1.z.string().min(1)).default([]),
    deviceAllowances: zod_1.z.array(exports.gameAccountAllowanceSchema).default([]),
    isActive: zod_1.z.boolean().default(true),
    email: zod_1.z.string().trim().email('Enter a valid email.').max(160).nullable().optional(),
    /** Stored encrypted beside the account password; omit for none. */
    emailPassword: zod_1.z.string().min(1).max(200).nullable().optional(),
    notes: zod_1.z.string().trim().max(500).nullable().optional(),
    /** The PC it was made for; omit or null for a venue-wide account. */
    homeDeviceId: zod_1.z.string().min(1).nullable().optional(),
});
exports.updateGameAccountSchema = zod_1.z.object({
    accountName: zod_1.z.string().trim().min(1).max(80).optional(),
    username: zod_1.z.string().trim().min(1).max(120).optional(),
    /** Omit to leave the stored ciphertext untouched. */
    password: zod_1.z.string().min(1).max(200).optional(),
    company: zod_1.z.string().trim().min(1).max(60).optional(),
    gameIds: zod_1.z.array(zod_1.z.string().min(1)).optional(),
    deviceAllowances: zod_1.z.array(exports.gameAccountAllowanceSchema).optional(),
    isActive: zod_1.z.boolean().optional(),
    email: zod_1.z.string().trim().email('Enter a valid email.').max(160).nullable().optional(),
    /** Omit to leave the stored ciphertext untouched; null clears it. */
    emailPassword: zod_1.z.string().min(1).max(200).nullable().optional(),
    notes: zod_1.z.string().trim().max(500).nullable().optional(),
    /** Omit to leave it; null makes the account venue-wide again. */
    homeDeviceId: zod_1.z.string().min(1).nullable().optional(),
});
/**
 * Bulk edits on the game-accounts page (owner, 2026-09-23): tick many
 * accounts, then do one thing to all of them. Capped at 500 like every other
 * selection — a bound on a client that loops, not on a real venue.
 */
const gameAccountIdSelection = zod_1.z
    .array(zod_1.z.string().min(1))
    .min(1, 'Select at least one account.')
    .max(500)
    .transform((ids) => [...new Set(ids)]);
/**
 * Add ONE game to every selected account's "games this account owns". Adds
 * only — an account that already owns it is left as it is, and no other game
 * is touched.
 */
exports.bulkLinkGameAccountsSchema = zod_1.z.object({
    accountIds: gameAccountIdSelection,
    gameId: zod_1.z.string().min(1),
});
/**
 * Set the PC each account was made for, many at once. `deviceId: null` makes
 * that account venue-wide. One entry per account: two different answers for
 * the same account would leave the server choosing which one was meant.
 */
exports.bulkAssignHomeDevicesSchema = zod_1.z.object({
    assignments: zod_1.z
        .array(zod_1.z.object({
        accountId: zod_1.z.string().min(1),
        deviceId: zod_1.z.string().min(1).nullable(),
    }))
        .min(1, 'Nothing to assign.')
        .max(500)
        .refine((rows) => new Set(rows.map((row) => row.accountId)).size === rows.length, 'Each account may appear only once.'),
});
/** Which of an account's two secrets a reveal asks for. */
exports.GAME_ACCOUNT_SECRET_FIELDS = ['password', 'emailPassword'];
/**
 * A reveal with no body at all is the launcher password: that is what every
 * staff tab built before the mailbox field existed sends, and Express 5 hands
 * a body-less POST to the pipe as `undefined`, which a bare object schema
 * would refuse. Explicit, not `.default()` — zod 4 returns a `.default()`
 * value without parsing it, and an unparsed `{}` would carry no `field`.
 */
const revealGameAccountBodySchema = zod_1.z.object({
    field: zod_1.z.enum(exports.GAME_ACCOUNT_SECRET_FIELDS).default('password'),
});
exports.revealGameAccountSchema = zod_1.z.preprocess((value) => (value === undefined || value === null ? {} : value), revealGameAccountBodySchema);
/**
 * The accounts vault password (owner, 2026-09-08): what a PC asks for before
 * it shows a venue account's secrets. Set by the OWNER only. Long and mixed
 * on purpose — it guards every launcher and mailbox password the venue owns.
 */
exports.ACCOUNT_VAULT_PASSWORD_MIN_LENGTH = 10;
exports.setAccountVaultPasswordSchema = zod_1.z.object({
    password: zod_1.z
        .string()
        .min(exports.ACCOUNT_VAULT_PASSWORD_MIN_LENGTH, 'Use at least 10 characters.')
        .max(200)
        .refine((value) => /[0-9]/.test(value) && /[A-Za-z]/.test(value), {
        message: 'Use letters and numbers.',
    }),
});
exports.gameAccountListQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().max(120).optional(),
    company: zod_1.z.string().trim().max(60).optional(),
    includeInactive: zod_1.z.coerce.boolean().default(false),
});
exports.devicePathSchema = zod_1.z.object({
    deviceId: zod_1.z.string().min(1),
    /** A full path on that PC or a launcher link — see `launch-target.ts`. */
    path: launch_target_1.launchTargetString.pipe(zod_1.z.string().min(1, 'Path is required.')),
    installed: zod_1.z.boolean().default(true),
});
exports.createGameSchema = zod_1.z.object({
    title: zod_1.z.string().trim().min(1, 'Title is required.').max(160),
    company: zod_1.z.string().trim().max(60).optional().nullable(),
    /**
     * Default path or launch link, used when this device has no override: a
     * file for a title installed on the PCs, a Steam / Store / Epic / Riot /
     * Battle.net / Ubisoft / EA link for one that lives in a launcher.
     */
    executablePath: launch_target_1.launchTargetString.optional().nullable(),
    gameCategoryId: zod_1.z.string().min(1).optional().nullable(),
    coverMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    trailerMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    devicePaths: zod_1.z.array(exports.devicePathSchema).default([]),
    isFeatured: zod_1.z.boolean().default(false),
    isActive: zod_1.z.boolean().default(true),
    sortOrder: zod_1.z.number().int().min(0).default(0),
});
exports.updateGameSchema = zod_1.z.object({
    title: zod_1.z.string().trim().min(1).max(160).optional(),
    company: zod_1.z.string().trim().max(60).optional().nullable(),
    executablePath: launch_target_1.launchTargetString.optional().nullable(),
    gameCategoryId: zod_1.z.string().min(1).optional().nullable(),
    coverMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    trailerMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    /** Replaces the whole override set when present. */
    devicePaths: zod_1.z.array(exports.devicePathSchema).optional(),
    isFeatured: zod_1.z.boolean().optional(),
    isActive: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
exports.gameListQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().max(160).optional(),
    gameCategoryId: zod_1.z.string().min(1).optional(),
    includeInactive: zod_1.z.coerce.boolean().default(false),
});
exports.createAppSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Name is required.').max(80),
    executablePath: launch_target_1.launchTargetString.optional().nullable(),
    iconMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    devicePaths: zod_1.z.array(exports.devicePathSchema).default([]),
    isActive: zod_1.z.boolean().default(true),
    sortOrder: zod_1.z.number().int().min(0).default(0),
});
exports.updateAppSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(80).optional(),
    executablePath: launch_target_1.launchTargetString.optional().nullable(),
    iconMediaAssetId: zod_1.z.string().min(1).optional().nullable(),
    devicePaths: zod_1.z.array(exports.devicePathSchema).optional(),
    isActive: zod_1.z.boolean().optional(),
    sortOrder: zod_1.z.number().int().min(0).optional(),
});
exports.appListQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().max(80).optional(),
    includeInactive: zod_1.z.coerce.boolean().default(false),
});
/**
 * Resolves the executable path for one device: its override, else the default
 * (SPEC §6.6 "Launch resolves this device's override, else the default").
 *
 * Exported so the API, the portal and the agent cannot each invent a slightly
 * different fallback rule.
 */
function resolveExecutablePath(defaultPath, devicePaths, deviceId) {
    const override = devicePaths.find((entry) => entry.deviceId === deviceId);
    if (override && override.path.trim().length > 0)
        return override.path;
    return defaultPath && defaultPath.trim().length > 0 ? defaultPath : null;
}
/*
 * §6.8 Device Groups — REMOVED 2026-09-24 with the page (owner: "remove ...
 * device groups page"). Production held no group. The DeviceGroup tables stay,
 * and the Screens/TV code still honours a group's idle video if a row exists,
 * but nothing can create one any more.
 */
/* -------------------------------------------------------------------------- */
/* §6.8 Payment providers — electronic tender (FIB, Qi, FastPay)              */
/* -------------------------------------------------------------------------- */
/**
 * The e-payment providers a venue can take money through (SPEC §6.8; owner,
 * 2026-09-23: make the Payment providers page work). `OTHER` is for a wallet
 * or card terminal the list does not name yet — the tender is recorded the
 * same way whoever runs it.
 *
 * WHAT "WORKS" MEANS HERE: a configured, ACTIVE provider becomes a tender the
 * till and the refill dialog can choose (`PaymentMethod` `ELECTRONIC` plus
 * `electronicsProviderId`). The cashier takes the payment on the provider's
 * own terminal or app; this system records which provider it went through, so
 * reports and box handovers can separate it from cash. It does NOT call the
 * provider's API — there is no gateway integration, and the credentials are
 * stored for the day there is one.
 *
 * Routes (`settings.manage`): GET/POST `/settings/electronics`, PATCH/DELETE
 * `/settings/electronics/:id`. The tender list the till reads is
 * `GET /electronic-tenders` — see {@link ElectronicTenderOptionDto}.
 */
exports.EPAYMENT_PROVIDERS = ['FIB', 'QI', 'FASTPAY', 'OTHER'];
/**
 * A write-only credential on UPDATE: omit or send blank to leave the stored
 * value untouched, send null to clear it. Blank-as-unchanged is deliberate —
 * the edit form cannot show the stored key, so its box starts empty, and
 * saving a label change must not wipe a credential nobody retyped.
 */
const writeOnlyCredentialUpdate = zod_1.z
    .string()
    .max(500)
    .nullable()
    .optional()
    .transform((value) => (typeof value === 'string' && value.trim() === '' ? undefined : value));
exports.createElectronicsProviderSchema = zod_1.z.object({
    provider: zod_1.z.enum(exports.EPAYMENT_PROVIDERS),
    label: zod_1.z.string().trim().min(1, 'Label is required.').max(60),
    merchantId: zod_1.z.string().trim().max(120).optional().nullable(),
    baseUrl: zod_1.z.string().trim().max(300).optional().nullable(),
    /** Plaintext in transit only; encrypted before storage. Blank = none. */
    apiKey: zod_1.z
        .string()
        .max(500)
        .optional()
        .nullable()
        .transform((value) => (value?.trim() ? value : null)),
    /** Plaintext in transit only; encrypted before storage. Blank = none. */
    apiSecret: zod_1.z
        .string()
        .max(500)
        .optional()
        .nullable()
        .transform((value) => (value?.trim() ? value : null)),
    isActive: zod_1.z.boolean().default(true),
});
/** Explicit, never `.partial()` of the create schema (zod 4 keeps `.default()`s). */
exports.updateElectronicsProviderSchema = zod_1.z.object({
    label: zod_1.z.string().trim().min(1).max(60).optional(),
    merchantId: zod_1.z.string().trim().max(120).optional().nullable(),
    baseUrl: zod_1.z.string().trim().max(300).optional().nullable(),
    apiKey: writeOnlyCredentialUpdate,
    apiSecret: writeOnlyCredentialUpdate,
    isActive: zod_1.z.boolean().optional(),
});
/**
 * Image types accepted for logos, avatars, product shots, covers and device
 * photos. Deliberately a short allow-list: SVG is excluded because it can carry
 * script and is served from the same origin as the app.
 */
exports.ALLOWED_IMAGE_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'image/avif',
];
/** Idle-screen videos (SPEC §7). */
exports.ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm'];
exports.MAX_IMAGE_UPLOAD_BYTES = 8 * 1024 * 1024;
exports.MAX_VIDEO_UPLOAD_BYTES = 512 * 1024 * 1024;
/** Which media kinds are images; the rest are videos. Drives type/size limits. */
exports.IMAGE_MEDIA_KINDS = [
    'LOGO',
    'GAME_COVER',
    'APP_ICON',
    'PRODUCT_IMAGE',
    'CATEGORY_IMAGE',
    'PRIZE_IMAGE',
    'CHALLENGE_IMAGE',
    'AVATAR',
    'DEVICE_IMAGE',
    'ATTACHMENT',
    'WIFI_QR',
];
function isImageMediaKind(kind) {
    return exports.IMAGE_MEDIA_KINDS.includes(kind);
}
/** The multipart field carrying the file. */
exports.MEDIA_UPLOAD_FIELD = 'file';
exports.mediaUploadQuerySchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.MEDIA_ASSET_KINDS),
});
//# sourceMappingURL=settings.js.map